/**
 * learning/hubModel.ts — the Learning Hub's pure model: the intent tiles and their per-level copy, the
 * topic grouping for the Microsoft Learn feed, and the training's step names.
 *
 * NO JSX, NO FETCH. Extracted from LearningHub.tsx so the Kombi Learning page renders the SAME tiles
 * with the SAME wording and groups topics by the SAME rules. The alternative was a second copy of
 * twenty-one hint strings and the canonical-topic map, which would drift the first time either page
 * was edited — and the drift would be invisible, because both would still look correct in isolation.
 */
import type { CompetencyLevel, LearnItem } from './api';

/**
 * The training's stop names, for the journey's accessible list. Generated from the step COUNT because
 * that is all this card is given: the hub payload carries TrainingProgress (percent, stepsCompleted,
 * stepCount), and the step titles live behind a different endpoint the card does not call. So the
 * names are honest and generic rather than invented — "Step 3 of 7" is true, a guessed title would
 * not be. They are never shown on screen (showLabels={false}); they exist so a screen reader hears a
 * list of steps with the current one marked, instead of unnamed items.
 */
export const TRAINING_STOPS = (count: number): string[] =>
  Array.from({ length: Math.max(count, 0) }, (_, i) => `Step ${i + 1} of ${count}`);

export interface IntentTile {
  title: string;
  to: string;
  /**
   * An OUTBOUND link that leaves the platform (opens in a new tab, rel="noreferrer") — for Microsoft's
   * own learning sites. A browser deep-link only; we never fetch it. Internal app routes leave this
   * false/absent and render as ordinary same-tab links.
   */
  external?: boolean;
  /** A one-line hint per level (LH-F09 framing). */
  hint: Record<CompetencyLevel, string>;
}

/**
 * Microsoft's own Power Automate learning front door — where "Build a tool" now points (decision #6).
 * An outbound browser deep-link on the allowlisted MS Learn host; never fetched server-side.
 */
const POWER_AUTOMATE_LEARN_URL = 'https://learn.microsoft.com/power-automate/';

/**
 * The intent tiles, in journey order. `mostPopularToolTo` threads in the "most popular tool this week"
 * link the hub computed (decision #6): when the week had a qualifying tool, the two "see what people
 * built" tiles deep-link to it; otherwise they fall back to the full catalogue so the link is never
 * dangling (PC-F14). Everything else is static content.
 */
export function buildIntentTiles(mostPopularToolTo: string): readonly IntentTile[] {
  return [
    {
      title: 'Build a tool',
      to: POWER_AUTOMATE_LEARN_URL,
      external: true,
      hint: {
        Beginner:
          "Start on Microsoft's own Power Automate learning site — build your first flow step by step.",
        Practitioner:
          "Learn to turn a repeatable task into a flow on Microsoft's Power Automate site.",
        Advanced:
          'Go deep on Power Automate on Microsoft Learn — patterns built to travel safely between teams.',
      },
    },
    {
      title: 'Share a tool',
      to: '/share',
      hint: {
        Beginner: 'Ready to share what you built? Every tool is reviewed before publication.',
        Practitioner:
          'Publish a tool for colleagues to adopt — review protects you as much as them.',
        Advanced: 'Publish reusable, parameterised solutions for adoption at scale.',
      },
    },
    {
      title: "See what's possible",
      to: mostPopularToolTo,
      hint: {
        Beginner: 'See the tool colleagues adopted most this week to spark ideas.',
        Practitioner: "Look at what's catching on this week for patterns you can adapt.",
        Advanced: "See what's being adopted right now before you build — reuse beats rebuilding.",
      },
    },
    {
      title: 'See real examples',
      to: mostPopularToolTo,
      hint: {
        Beginner:
          'The real, approved tool colleagues adopted most this week — a place to start from.',
        Practitioner: "This week's most-adopted tool, ready to adopt and adapt for your team.",
        Advanced: "Study this week's most-adopted build and how it was governed.",
      },
    },
    {
      title: 'How to use the platform',
      to: '/learning/tour',
      hint: {
        Beginner: 'A short guided tour of how Trailblazer works, end to end.',
        Practitioner:
          'A quick tour of the platform — where everything lives and how it fits together.',
        Advanced:
          'A guided tour of the platform, for when you need the map rather than the detail.',
      },
    },
    {
      title: 'Learn in person',
      to: '#learn-with-people',
      hint: {
        Beginner: 'Prefer a person? Your department support champion can help.',
        Practitioner: 'Get hands-on help from your department support champion.',
        Advanced: 'Connect with your support champion to unblock a harder build.',
      },
    },
    {
      title: 'Browse everything',
      to: '/tools',
      hint: {
        Beginner: 'Ignore the tuning and see the whole catalogue — nothing is hidden.',
        Practitioner: 'See the whole catalogue, unfiltered by level.',
        Advanced: 'The full, unfiltered catalogue — no personalisation applied.',
      },
    },
  ];
}

const LEARN_ROLE_IDS: ReadonlySet<string> = new Set([
  'administrator',
  'developer',
  'business-user',
  'functional-consultant',
  'solution-architect',
  'technology-manager',
  'student',
  'maker',
  'business-owner',
  'security-engineer',
  'data-engineer',
  'devops-engineer',
  'k-12-educator',
  'higher-ed-educator',
  'security-operations-analyst',
  'ai-engineer',
  'data-analyst',
  'school-leader',
  'data-scientist',
  'business-analyst',
  'database-administrator',
  'business-leader',
  'startup-founder',
  'service-adoption-specialist',
  'platform-engineer',
  'auditor',
  'support-engineer',
  'risk-practitioner',
  'network-engineer',
  'identity-access-admin',
  'privacy-manager',
  'ai-edge-engineer',
  'parent-guardian',
  'ip-admin',
  'technical-writer',
]);

/**
 * Merge Microsoft's several near-identical Copilot product slugs into ONE topic. They are distinct
 * ids upstream (`ms-copilot`, `microsoft-365-copilot`, `microsoft-365-copilot-chat`,
 * `microsoft-copilot`, `microsoft-copilot-studio`) but the same topic to a person browsing — five
 * "Copilot …" pills is exactly the clutter we are removing. Any slug here collapses to 'copilot'.
 */
const COPILOT_SLUGS: ReadonlySet<string> = new Set([
  'ms-copilot',
  'microsoft-copilot',
  'microsoft-365-copilot',
  'microsoft-365-copilot-chat',
  'microsoft-copilot-studio',
  'copilot-studio',
]);

/** Human labels for the product slugs we commonly carry; anything unlisted is title-cased generically. */
const PRODUCT_LABELS: Readonly<Record<string, string>> = {
  copilot: 'Copilot',
  'power-platform': 'Power Platform',
  'power-apps': 'Power Apps',
  'power-automate': 'Power Automate',
  'power-bi': 'Power BI',
  'power-pages': 'Power Pages',
  dataverse: 'Dataverse',
  m365: 'Microsoft 365',
  'office-365': 'Microsoft 365',
  'office-teams': 'Microsoft Teams',
  azure: 'Azure',
  'dynamics-365': 'Dynamics 365',
  github: 'GitHub',
  'entra-id': 'Microsoft Entra',
};

/** Title-case an unlisted slug: 'azure-virtual-machines' → 'Azure Virtual Machines'. */
function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .map((w) => (w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Normalise one raw product slug to its canonical topic key (Copilot variants → 'copilot'). */
function canonicalTopic(slug: string): string {
  return COPILOT_SLUGS.has(slug) ? 'copilot' : slug;
}

/** A pill: one canonical topic key, its human label, and every raw slug that maps to it (for filtering). */
export interface Topic {
  key: string;
  label: string;
  slugs: string[];
}

/**
 * Build the compact topic list from the items' productTags: drop role ids, collapse Copilot variants
 * to one key, humanize the labels, and dedupe. Ordered by how many items carry the topic (busiest
 * first) so the most useful pills lead. Still a FLAT, level-agnostic set — hides nothing (LH-NFR03).
 */
export function buildTopics(items: LearnItem[]): Topic[] {
  const byKey = new Map<string, { slugs: Set<string>; count: number }>();
  for (const it of items) {
    // A topic is counted once per item even if the item has several slugs mapping to it.
    const keysInItem = new Set<string>();
    for (const raw of it.productTags) {
      if (LEARN_ROLE_IDS.has(raw)) continue; // a role is not a topic
      keysInItem.add(canonicalTopic(raw));
    }
    for (const key of keysInItem) {
      const entry = byKey.get(key) ?? { slugs: new Set<string>(), count: 0 };
      entry.count += 1;
      byKey.set(key, entry);
    }
    // Record which raw slugs feed each key so the filter can match any of them.
    for (const raw of it.productTags) {
      if (LEARN_ROLE_IDS.has(raw)) continue;
      byKey.get(canonicalTopic(raw))!.slugs.add(raw);
    }
  }
  return Array.from(byKey.entries())
    .map(([key, { slugs, count }]) => ({
      key,
      label: PRODUCT_LABELS[key] ?? humanizeSlug(key),
      slugs: Array.from(slugs),
      count,
    }))
    .sort((a, b) => (b.count !== a.count ? b.count - a.count : a.label.localeCompare(b.label)))
    .map(({ key, label, slugs }) => ({ key, label, slugs }));
}
